import classNames from "classnames";
import styles from "./AdtechBanner.module.scss";
import { ESIInclude } from "@packages/esi-include/ESIInclude";
import { StandardProps } from "@/types/types";
import { Breadcrumb, FacetsInformation } from "@/types/productListing";
import { buildBannerPathname } from "./utils";
import { shouldShowAdtechBanner } from "@/utils/banners";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useFeatureFlags } from "@/flags/flags";

type BannerProps = {
  pathname: string;
  adtechBannerService: string;
  facetsInformation: FacetsInformation;
  crumbs?: Breadcrumb[];
  searchTerm?: string;
  topBannerId?: string;
};

const JL_EVENTS_ADTECH_BANNER_CLICK = "jl-events.adtech.bannerClick";

const dispatchClickEvent = () => {
  window.dispatchEvent(new CustomEvent(JL_EVENTS_ADTECH_BANNER_CLICK));
};

function AdtechBrowseBanner({
  pathname,
  adtechBannerService,
  facetsInformation,
  crumbs,
}: BannerProps) {
  const { appliedProductTypes, isFilteredByBrand } = facetsInformation;
  const featureFlags = useFeatureFlags();
  if (!Array.isArray(crumbs) || !crumbs?.length) return null;

  const formattedBreadcrumbs = encodeURIComponent(
    crumbs.map((b) => b.displayName.trim()).join("|"),
  );

  const appliedProductTypesParam =
    Array.isArray(appliedProductTypes) && appliedProductTypes.length
      ? `&appliedProductTypes=${appliedProductTypes.join("|")}`
      : "";

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={styles.adtechbanner}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={
          `${adtechBannerService}/browse` +
          `?browsePageHierarchy=${formattedBreadcrumbs}` +
          `&brandFilterApplied=${String(isFilteredByBrand)}` +
          `&pathname=${pathname}` +
          `${appliedProductTypesParam}` +
          `${featureFlags?.isWideFlagOn?.value ? `&isWide=true` : ""}`
        }
        id="esiAdtechBanner"
      />
    </aside>
  );
}

function AdtechSearchBanner({
  pathname,
  adtechBannerService,
  facetsInformation,
  searchTerm,
  topBannerId,
}: BannerProps) {
  const { isFiltered } = facetsInformation;
  const featureFlags = useFeatureFlags();
  if (topBannerId || !searchTerm) {
    return null;
  }

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanner)}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={
          `${adtechBannerService}/search` +
          `?searchTerm=${encodeURIComponent(searchTerm.toString())}` +
          `&filterApplied=${String(isFiltered)}` +
          `&pathname=${pathname}` +
          `${featureFlags?.isWideFlagOn?.value ? `&isWide=true` : ""}`
        }
        id="esiAdtechBanner"
      />
    </aside>
  );
}

function AdtechBrandBanner({
  adtechBannerService,
  brandName,
}: {
  adtechBannerService: string;
  brandName: string | undefined;
}) {
  const featureFlags = useFeatureFlags();
  if (!brandName) return null;

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanner)}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={
          `${adtechBannerService}/brand?brandName=${encodeURIComponent(brandName)}` +
          `${featureFlags?.isWideFlagOn?.value ? `&isWide=true` : ""}`
        }
        id="esiAdtechBanner"
      />
    </aside>
  );
}

export function AdtechBanner({
  runtimeConfig,
  productListingData,
}: Pick<StandardProps, "runtimeConfig" | "productListingData">) {
  const { pageType, query, facetId } = useRouteParams();

  const { adtechBannerService } = runtimeConfig;
  const { crumbs, facetsInformation, banners } = productListingData;

  const pathname = buildBannerPathname({ query, facetId });

  const showAdtechBanner = shouldShowAdtechBanner(
    pageType,
    banners?.topBannerId,
  );

  if (!showAdtechBanner) {
    return null;
  }

  if (pageType === "browse") {
    return (
      <AdtechBrowseBanner
        pathname={pathname}
        adtechBannerService={adtechBannerService}
        facetsInformation={facetsInformation}
        crumbs={crumbs}
      />
    );
  }

  if (pageType === "search" && query["search-term"]) {
    return (
      <AdtechSearchBanner
        pathname={pathname}
        adtechBannerService={adtechBannerService}
        facetsInformation={facetsInformation}
        searchTerm={
          Array.isArray(query["search-term"])
            ? query["search-term"][0]
            : query["search-term"]
        }
        topBannerId={banners?.topBannerId}
      />
    );
  }

  if (pageType === "brand") {
    return (
      <AdtechBrandBanner
        brandName={productListingData.pageInformation.brandName}
        adtechBannerService={adtechBannerService}
      />
    );
  }

  return null;
}
